import { cancelQuery, submitQuery } from '@/api/endpoints';
import { streamQuery } from '@/api/sse';
import { ApiError } from '@/api/client';
import type {
  ColumnSchema,
  DucklingTier,
  QueryError,
  QueryStats,
  ResultRow,
  SseEvent,
} from '@/types/api';
import type { LocalQueryResult, RemoteExecutor } from '@/cache/types';
import { normalizeColumnSchemas } from '@/lib/values';

export interface ExecuteProgress {
  readonly rowsScanned: number;
  readonly bytesScanned: number;
  readonly fraction: number | null;
}

export interface ExecuteCallbacks {
  readonly onQueryId?: (queryId: string) => void;
  readonly onSchema?: (columns: readonly ColumnSchema[]) => void;
  readonly onProgress?: (progress: ExecuteProgress) => void;
  /** Fired per batch so long results can stream into the grid. */
  readonly onBatch?: (rows: readonly ResultRow[], total: number) => void;
}

export interface ExecuteOptions extends ExecuteCallbacks {
  readonly tier?: DucklingTier;
  readonly datasetScope?: string;
  readonly maxRows?: number;
  readonly signal?: AbortSignal;
}

export interface ExecuteResult {
  readonly queryId: string;
  readonly columns: readonly ColumnSchema[];
  readonly rows: readonly ResultRow[];
  readonly truncated: boolean;
  readonly stats: QueryStats | null;
}

/** A query that failed. Carries SQLSTATE and the error position for squiggles. */
export class QueryFailure extends Error {
  readonly queryError: QueryError;

  constructor(queryError: QueryError) {
    super(queryError.message);
    this.name = 'QueryFailure';
    this.queryError = queryError;
  }
}

export class QueryCancelled extends Error {
  constructor() {
    super('Query cancelled.');
    this.name = 'QueryCancelled';
  }
}

function toQueryError(cause: unknown): QueryError {
  if (cause instanceof QueryFailure) return cause.queryError;
  if (cause instanceof ApiError) {
    return {
      message: cause.message,
      sqlState: cause.sqlState ?? (cause.status === 503 ? '53300' : '58000'),
      position: null,
      detail: null,
      hint:
        cause.retryAfterSeconds !== undefined
          ? `Retry in ${cause.retryAfterSeconds}s.`
          : null,
    };
  }
  return {
    message: cause instanceof Error ? cause.message : String(cause),
    sqlState: '58000',
    position: null,
    detail: null,
    hint: null,
  };
}

export function asQueryError(cause: unknown): QueryError {
  return toQueryError(cause);
}

/**
 * Submits a query and drains its SSE stream to completion.
 *
 * A failed query throws. It is never returned as a successful empty result --
 * see spec §6, and the CustomDataPool regression it cites.
 */
export async function executeQuery(sql: string, options: ExecuteOptions = {}): Promise<ExecuteResult> {
  const request = {
    sql,
    ...(options.tier ? { tier: options.tier } : {}),
    ...(options.datasetScope ? { datasetScope: options.datasetScope } : {}),
    ...(options.maxRows !== undefined ? { maxRows: options.maxRows } : {}),
  };

  const { queryId } = await submitQuery(request);
  options.onQueryId?.(queryId);

  let columns: readonly ColumnSchema[] = [];
  const rows: ResultRow[] = [];
  let truncated = false;
  let stats: QueryStats | null = null;
  let failure: QueryError | null = null;
  let completed = false;

  const handle = (event: SseEvent): void => {
    switch (event.type) {
      case 'schema':
        // Normalised at the boundary: a single malformed column descriptor must
        // not be able to take the application down when it reaches the grid.
        columns = normalizeColumnSchemas(event.columns);
        options.onSchema?.(columns);
        break;
      case 'batch':
        rows.push(...event.rows);
        options.onBatch?.(event.rows, rows.length);
        break;
      case 'progress':
        options.onProgress?.({
          rowsScanned: event.rowsScanned,
          bytesScanned: event.bytesScanned,
          fraction: event.fraction,
        });
        break;
      case 'complete':
        truncated = event.truncated;
        stats = event.stats;
        completed = true;
        break;
      case 'error':
        failure = event.error;
        break;
      default:
        break;
    }
  };

  try {
    const streamOptions = options.signal ? { signal: options.signal } : {};
    await streamQuery(queryId, handle, streamOptions);
  } catch (cause) {
    if (options.signal?.aborted) {
      void cancelQuery(queryId).catch(() => undefined);
      throw new QueryCancelled();
    }
    throw new QueryFailure(toQueryError(cause));
  }

  if (failure) throw new QueryFailure(failure);

  if (!completed) {
    // The stream ended without a terminal event. Treating this as an empty
    // success would be exactly the failure mode §6 forbids.
    throw new QueryFailure({
      message: 'The result stream ended before the query completed.',
      sqlState: '08006',
      position: null,
      detail: null,
      hint: 'The duckling may have been terminated. Run the query again.',
    });
  }

  return { queryId, columns, rows, truncated, stats };
}

/** Adapts `executeQuery` to the cache's remote-fallback interface. */
export function createRemoteExecutor(tier?: DucklingTier): RemoteExecutor {
  return {
    async run(sql: string, signal?: AbortSignal): Promise<LocalQueryResult> {
      const result = await executeQuery(sql, {
        ...(tier ? { tier } : {}),
        ...(signal ? { signal } : {}),
      });
      return { columns: result.columns, rows: result.rows };
    },
  };
}
